import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../common/LoadingSpinner';
import Pagination from '../../common/Pagination';
import instituteService from '../../../services/instituteService';
import SocietySectionToolbar from './SocietySectionToolbar';
import SectionFilterSelect from './SectionFilterSelect';
import { getAdCoverImageUrl, IMAGE_PLACEHOLDER_PATH } from '../../../utils/imageFallback';

const SocietyInstitutes = ({ society, onError }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    sort: 'newest'
  });

  // Load institutes
  useEffect(() => {
    const loadInstitutes = async () => {
      setLoading(true);
      try {
        const params = {
          page: currentPage,
          page_size: 12,
          society: society.id,
        };

        // Add search filter
        if (filters.search) {
          params.search = filters.search;
        }

        // Add type filter
        if (filters.type !== 'all') {
          params.institute_type = filters.type;
        }

        // Add sorting
        if (filters.sort === 'newest') {
          params.ordering = '-id';
        } else if (filters.sort === 'oldest') {
          params.ordering = 'id';
        } else if (filters.sort === 'name') {
          params.ordering = 'name';
        }

        const response = await instituteService.getInstitutes(params);
        
        // Handle paginated response
        if (response.results) {
          setInstitutes(response.results);
          setTotalPages(Math.ceil((response.count || 0) / 12));
          setTotalCount(response.count || 0);
        } else {
          // Handle non-paginated response
          const institutesList = Array.isArray(response) ? response : [];
          setInstitutes(institutesList);
          setTotalPages(1);
          setTotalCount(institutesList.length);
        }
      } catch (error) {
        console.error('Error loading institutes:', error);
        if (onError) {
          onError('Failed to load institutes');
        }
        setInstitutes([]);
        setTotalPages(1);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    if (society?.id) {
      loadInstitutes();
    }
  }, [society?.id, currentPage, filters, onError]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <svg
        key={i}
        className={`w-4 h-4 ${
          i < Math.floor(rating) ? 'text-yellow-400' : 'text-gray-300'
        }`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  const getTypeColor = (type) => {
    const colors = {
      school: 'bg-blue-100 text-blue-800',
      technical: 'bg-green-100 text-green-800',
      college: 'bg-purple-100 text-purple-800',
      university: 'bg-orange-100 text-orange-800',
      training: 'bg-yellow-100 text-yellow-800'
    };
    return colors[type] || colors.school;
  };

  // Safety check
  if (!society) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="text-center text-gray-500">Loading society information...</div>
      </div>
    );
  }

  const activeFilterCount =
    (filters.type !== 'all' ? 1 : 0) + (filters.sort !== 'newest' ? 1 : 0);

  return (
    <div className="space-y-4 sm:space-y-6">
      <SocietySectionToolbar
        title="Institutes"
        count={totalCount}
        itemLabel={{ singular: 'institute', plural: 'institutes' }}
        societyName={society.name}
        searchPlaceholder="Search institutes..."
        searchValue={filters.search}
        onSearchChange={(v) => handleFilterChange({ ...filters, search: v })}
        activeFilterCount={activeFilterCount}
        addButton={
          isAuthenticated
            ? {
                label: 'Add Institute',
                onClick: () =>
                  navigate(`/societies/${society.slug}/institutes/create?society=${society.id}`),
              }
            : null
        }
      >
        <SectionFilterSelect
          label="Type"
          value={filters.type}
          onChange={(e) => handleFilterChange({ ...filters, type: e.target.value })}
        >
          <option value="all">All Types</option>
          <option value="school">School</option>
          <option value="technical">Technical</option>
          <option value="college">College</option>
          <option value="university">University</option>
          <option value="training">Training Center</option>
        </SectionFilterSelect>
        <SectionFilterSelect
          label="Sort By"
          value={filters.sort}
          onChange={(e) => handleFilterChange({ ...filters, sort: e.target.value })}
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="highest_rated">Highest Rated</option>
          <option value="name">Name A-Z</option>
        </SectionFilterSelect>
      </SocietySectionToolbar>

      {/* Institutes Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3 sm:gap-6">
        {loading ? (
          <div className="col-span-full flex justify-center py-8">
            <LoadingSpinner size="medium" />
          </div>
        ) : institutes.length > 0 ? (
          institutes.map((institute) => (
            <div 
              key={institute.id} 
              className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer"
              onClick={() => navigate(`/institutes/${institute.id}`)}
            >
              {/* Institute Image */}
              <div className="h-48 bg-gray-200 flex items-center justify-center">
                <img
                  src={getAdCoverImageUrl(institute)}
                  alt={institute.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = IMAGE_PLACEHOLDER_PATH;
                  }}
                />
              </div>

              {/* Institute Info */}
              <div className="p-6">
                <div className="mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
                    {institute.name}
                  </h3>
                </div>

                {(institute.address || institute.city) && (
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {[institute.address, institute.city].filter(Boolean).join(', ')}
                  </p>
                )}
                {institute.description && (
                  <p className="text-gray-500 text-sm mb-3 line-clamp-2">{institute.description}</p>
                )}

                <div className="flex items-center mb-3">
                  <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(institute.institute_type || 'school')}`}>
                    {(institute.institute_type || 'school').charAt(0).toUpperCase() + (institute.institute_type || 'school').slice(1)}
                  </span>
                </div>

                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="line-clamp-1">
                      {[institute.address, institute.city].filter(Boolean).join(', ')}
                    </span>
                  </div>
                  {institute.phone && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span>{institute.phone}</span>
                    </div>
                  )}
                </div>

                <div className="flex space-x-2">
                  <button className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors duration-200">
                    View Details
                  </button>
                  {institute.website && (
                    <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors duration-200">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full bg-white rounded-lg shadow-sm border p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No institutes yet</h3>
            <p className="text-gray-500 mb-4">Be the first to add an institute to this society.</p>
            {isAuthenticated && (
              <button 
                onClick={() => navigate(`/societies/${society.slug}/institutes/create?society=${society.id}`)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
              >
                Add First Institute
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default SocietyInstitutes;
